"use client";

import { useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import {
    ACTIVITY_VISIBLE,
    activityLegend,
    cardFragments,
    eventText,
    formatAbsolute,
    hasCardLine,
    relativeTime,
    USAGE_WINDOW_DAYS,
    verificationsNote,
    type ActivityEvent,
    type CouponActivity,
} from "@/lib/verification-history";
import type { CardHistory } from "@/lib/client-props";
import "./store/store-v5.css";

/** What the shopper clicked: a code ("Get Code") or a deal ("Get Deal"). */
export type OfferKind = "code" | "deal";

interface Props {
    /** Summary built on the server from real conclusive runs, valid shopper reports and human usage; undefined = nothing known. */
    history?: CardHistory;
    offerKind?: OfferKind;
}

type Load = { state: "idle" } | { state: "loading" } | { state: "ready"; activity: CouponActivity } | { state: "error" };

// The page is ISR-cached: the server must not render clock-dependent text. The server snapshot is null (relative
// times fall back to "Oct 3"); in the browser the snapshot is the current minute, so after hydration every <time>
// re-renders as "14h ago" and keeps ticking once a minute.
const subscribeMinute = (onChange: () => void) => {
    const timer = setInterval(onChange, 60_000);
    return () => clearInterval(timer);
};
const minuteNow = () => Math.floor(Date.now() / 60_000);
const serverMinute = () => null;

function useNow(): Date | null {
    const minute = useSyncExternalStore(subscribeMinute, minuteNow, serverMinute);
    return minute === null ? null : new Date(minute * 60_000);
}

function Ago({ iso, now, className }: { iso: string; now: Date | null; className?: string }) {
    return (
        <time dateTime={iso} title={formatAbsolute(iso) ?? undefined} className={className}>
            {relativeTime(iso, now)}
        </time>
    );
}

function isActivity(body: unknown): body is CouponActivity {
    const b = body as CouponActivity | null;
    return !!b && typeof b === "object" && !!b.stats && Array.isArray(b.events);
}

/** Row marks: inline SVG, colours from the design tokens (CSS). Decorative next to the text, so labelled for AT. */
function CheckerMark() {
    return (
        <svg className="sp-history__mark" data-actor="checkout" viewBox="0 0 20 20" width="20" height="20" role="img" aria-label="Checkout test">
            <circle cx="10" cy="10" r="9.25" />
            <text x="10" y="10" dy="0.35em" textAnchor="middle">CS</text>
        </svg>
    );
}

function ShopperMark() {
    return (
        <svg className="sp-history__mark" data-actor="shopper" viewBox="0 0 20 20" width="20" height="20" role="img" aria-label="Shopper">
            <circle cx="10" cy="10" r="9.25" />
            <circle className="sp-history__glyph" cx="10" cy="7.6" r="3" />
            <path className="sp-history__glyph" d="M4.6 15.6c.9-2.6 3-4 5.4-4s4.5 1.4 5.4 4" />
        </svg>
    );
}

/** Merchant confirmation: a shield with a check. */
function MerchantMark() {
    return (
        <svg className="sp-history__mark" data-actor="merchant" viewBox="0 0 20 20" width="20" height="20" role="img" aria-label="Merchant">
            <circle cx="10" cy="10" r="9.25" />
            <path className="sp-history__glyph" d="M10 4.6l4.2 1.5v3.4c0 2.6-1.7 4.5-4.2 5.5-2.5-1-4.2-2.9-4.2-5.5V6.1z" />
            <path className="sp-history__glyph" d="M8.2 9.8l1.3 1.3 2.4-2.6" />
        </svg>
    );
}

function EventMark({ mark }: { mark: ReturnType<typeof eventText>["mark"] }) {
    if (mark === "checkout") return <CheckerMark />;
    if (mark === "merchant") return <MerchantMark />;
    return <ShopperMark />;
}

function EventItem({ event, now, hidden }: { event: ActivityEvent; now: Date | null; hidden?: boolean }) {
    const t = eventText(event);
    return (
        <li className="sp-history__event" data-tone={t.tone} data-kind={event.kind} hidden={hidden}>
            <EventMark mark={t.mark} />
            <p className="sp-history__what">
                {t.outcome ? <>{t.label} · <span className="sp-history__outcome">{t.outcome}</span></> : <span className="sp-history__outcome">{t.label}</span>}
            </p>
            {/* An unknown date (old protection with no record) shows no time at all. */}
            {event.at && <Ago iso={event.at} now={now} className="sp-history__when" />}
        </li>
    );
}

/**
 * Verification of ONE coupon (panel v2, 2026-10-24).
 *
 * Server HTML: ONE compact line built only from what the database proves — "Verified 14h ago" (newest conclusive run,
 * only when it worked; a merchant-confirmed offer without one leads with "Confirmed 2h ago", or "Confirmed by the
 * merchant" when the date is unknown) and "Used 19h ago" (newest human use, 180 days); the success percentage stays in the card
 * header ("X% Success"), never twice — and, when there is any evidence, a "History" button. No fragment -> no line;
 * no evidence -> nothing at all (never "not checked yet", never invented people or dates).
 *
 * The panel (stats + "Verification activity" timeline) is NOT in the HTML: it is fetched from
 * `/api/coupons/[id]/history` on the first click.
 */
export default function CouponVerificationHistory({ history, offerKind = "code" }: Props) {
    const [expanded, setExpanded] = useState(false);
    const [load, setLoad] = useState<Load>({ state: "idle" });
    const abort = useRef<AbortController | null>(null);
    const now = useNow();

    useEffect(() => () => abort.current?.abort(), []);

    const line = cardFragments(history);
    const showLine = hasCardLine(line);
    if (!history || (!showLine && !history.hasHistory)) return null;

    const panelId = `h-${history.couponId}`;

    const toggle = () => {
        const next = !expanded;
        setExpanded(next);
        if (next && load.state === "idle") {
            const controller = new AbortController();
            abort.current = controller;
            setLoad({ state: "loading" });
            fetch(`/api/coupons/${encodeURIComponent(history.couponId)}/history`, { signal: controller.signal })
                .then(res => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
                .then((body: unknown) => setLoad(isActivity(body) ? { state: "ready", activity: body } : { state: "error" }))
                .catch(err => {
                    if ((err as { name?: string })?.name === "AbortError") return;
                    setLoad({ state: "error" });
                });
        }
    };

    const fragments: ReactNode[] = [];
    if (line.confirmed) {
        fragments.push(
            <span key="c" className="sp-history__frag" data-kind="verified">
                {line.confirmedAt ? <>Confirmed <Ago iso={line.confirmedAt} now={now} /></> : "Confirmed by the merchant"}
            </span>,
        );
    }
    if (line.verifiedAt) fragments.push(<span key="v" className="sp-history__frag" data-kind="verified">Verified <Ago iso={line.verifiedAt} now={now} /></span>);
    if (line.usedAt) fragments.push(<span key="u" className="sp-history__frag">Used <Ago iso={line.usedAt} now={now} /></span>);

    return (
        <div className="sp-history">
            {showLine && (
                <p className="sp-history__line">
                    {fragments.map((f, i) => (
                        <span key={i}>
                            {i > 0 && <span className="sp-history__sep"> · </span>}
                            {f}
                        </span>
                    ))}
                </p>
            )}
            {history.hasHistory && (
                <>
                    <button
                        type="button"
                        className="sp-history__toggle"
                        aria-expanded={expanded}
                        aria-controls={panelId}
                        aria-label={`Verification history of this ${offerKind === "deal" ? "deal" : "code"}`}
                        onClick={toggle}
                    >
                        History
                    </button>
                    <div id={panelId} className="sp-history__panel" hidden={!expanded} aria-live="polite">
                        {expanded && load.state === "loading" && <p className="sp-history__status">Loading…</p>}
                        {expanded && load.state === "error" && <p className="sp-history__status">History unavailable.</p>}
                        {expanded && load.state === "ready" && <VerificationPanel activity={load.activity} now={now} panelId={panelId} />}
                    </div>
                </>
            )}
        </div>
    );
}

export function VerificationPanel({ activity, now, panelId }: { activity: CouponActivity; now: Date | null; panelId: string }) {
    const { stats, events } = activity;
    const [showAll, setShowAll] = useState(false);
    const legend = activityLegend(events);
    const more = Math.max(0, events.length - ACTIVITY_VISIBLE);
    const hasStats = stats.uses > 0 || stats.verifications > 0 || !!stats.lastUsedAt;
    if (!hasStats && events.length === 0) return <p className="sp-history__status">No activity recorded yet.</p>;
    return (
        <>
            {hasStats && (
                <dl className="sp-history__stats">
                    {stats.uses > 0 && (
                        <div>
                            <dt>Total uses</dt>
                            <dd>{stats.uses.toLocaleString("en-US")}</dd>
                            <dd className="sp-history__note">last {USAGE_WINDOW_DAYS} days</dd>
                        </div>
                    )}
                    {stats.verifications > 0 && (
                        <div>
                            <dt>Verifications</dt>
                            <dd>{stats.verifications.toLocaleString("en-US")}</dd>
                            <dd className="sp-history__note">{verificationsNote(events)}</dd>
                        </div>
                    )}
                    {stats.lastUsedAt && (
                        <div>
                            <dt>Last used</dt>
                            <dd><Ago iso={stats.lastUsedAt} now={now} /></dd>
                        </div>
                    )}
                </dl>
            )}
            {events.length > 0 && (
                <>
                    <p className="sp-history__heading" id={`${panelId}-activity`}>Verification activity</p>
                    {legend && <p className="sp-history__legend">{legend}</p>}
                    <ol className="sp-history__events" id={`${panelId}-list`} aria-labelledby={`${panelId}-activity`}>
                        {events.map((event, i) => <EventItem key={`${event.kind}-${event.at ?? "unknown"}-${i}`} event={event} now={now} hidden={!showAll && i >= ACTIVITY_VISIBLE} />)}
                    </ol>
                    {more > 0 && (
                        <button type="button" className="sp-history__more" aria-expanded={showAll} aria-controls={`${panelId}-list`} onClick={() => setShowAll(v => !v)}>
                            {showAll ? "Show fewer" : `Show ${more} more`}
                        </button>
                    )}
                </>
            )}
        </>
    );
}
